import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiCallError, clearSession, getToken } from './api/client';
import LoginPage from './pages/LoginPage';
// All other pages are lazy-loaded so PixiJS / Three.js and per-page bundles
// don't ship in the initial chunk. The first-paint cost is one fetch when the
// child navigates to a route they haven't visited yet; subsequent visits are
// served from cache.
// HomePage is now lazy-loaded inside ParkPage as the default "scene" tab
// (v0.14.0 PR-2.5). Removed from App.tsx after `/` started rendering
// ParkPage. Rollup still emits a single HomePage chunk shared by the
// `/` and any future direct consumers.
// PR-6 — ShopPage / ZooPage / GamesPage are lazy-loaded inside ParkPage
// as Park tab content. App.tsx no longer routes to them directly; the
// /shop /zoo /games paths just <Navigate /> into /?tab=*.
const PokedexPage = lazy(() => import('./pages/PokedexPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
// PR-7 — Quiz / Imagination move into LearnPage as tab content.
// /quiz and /imagine now redirect into /learn?tab=...
const MathBlitzPage = lazy(() => import('./pages/MathBlitzPage'));
const MemoryDigitsPage = lazy(() => import('./pages/MemoryDigitsPage'));
const PotionPuzzlePage = lazy(() => import('./pages/PotionPuzzlePage'));
const AgeMiniGamePage = lazy(() => import('./pages/AgeMiniGamePage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const BuildingInteriorPage = lazy(() => import('./pages/BuildingInteriorPage'));
const OnboardingHatchPage = lazy(() => import('./pages/OnboardingHatchPage'));
const MePage = lazy(() => import('./pages/MePage'));
const MeSettingsPage = lazy(() => import('./pages/MeSettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));
const DebugAcceleratePage = lazy(() => import('./pages/DebugAcceleratePage'));
const PetProfilePage = lazy(() => import('./pages/PetProfilePage'));
const DesignPreviewPage = lazy(() => import('./pages/DesignPreviewPage'));
const ParkPage = lazy(() => import('./pages/ParkPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const LearnPage = lazy(() => import('./pages/LearnPage'));
import { AppShellV2 } from './components/layout/AppShellV2';
import SleepOverlay from './components/SleepOverlay';
import UpdateBanner from './components/UpdateBanner';
import RewardBurst from './components/RewardBurst';
import LangSwitcher from './components/LangSwitcher';
import NicknameSetupModal from './components/NicknameSetupModal';
import BirthdaySurpriseModal from './components/BirthdaySurpriseModal';
import type { CareerStats, Child, Pet, PlayGate } from '@xiaopeiban/shared';
import { reportCurrentDeviceSoon } from './lib/deviceReport';
import { playSceneAmbience } from './lib/gameAudio';
import type { SceneAmbienceKey } from './lib/sceneAmbienceManifest';
import { setSleepThresholdMinutes, setPlayPeriod } from './lib/playSession';
import { applyForcedLang, setI18nAgeTier, t } from './i18n';
import { getAgeTierFromBirthDate } from '@xiaopeiban/shared';

/**
 * Map (route, active habitat scene) → ambience manifest key. HomePage scenes
 * follow the child's chosen habitat (meadow becomes day/night by clock time);
 * other routes get a per-page bed (chat = cozy room, quiz = library, …).
 * Returns null when we want the ambience layer silent.
 */
function resolveAmbienceKey(pathname: string, activeScene?: string): SceneAmbienceKey | null {
  // Page beds first — these only matter when we're not on the habitat home.
  if (pathname.startsWith('/chat')) return 'chat';
  if (pathname.startsWith('/quiz')) return 'quiz';
  if (pathname.startsWith('/shop') || pathname.startsWith('/adopt')) return 'shop';
  if (pathname.startsWith('/habits')) return 'habits';
  if (pathname.startsWith('/zoo')) return 'zoo';
  if (pathname.startsWith('/wallet')) return 'wallet';
  if (pathname.startsWith('/journal')) return 'journal';
  if (pathname.startsWith('/imagine')) return 'imagine';
  if (pathname.startsWith('/timeline')) return 'journal';
  // The settings/profile page contains the music picker. Keep the scene bed
  // silent there so switching styles auditions only the selected BGM track.
  if (pathname.startsWith('/me')) return null;
  // /, /ccc and any other route default to the active habitat.
  const scene = (activeScene || 'meadow').toLowerCase();
  if (scene === 'meadow') {
    const h = new Date().getHours();
    return h >= 19 || h < 6 ? 'meadow_night' : 'meadow_day';
  }
  // Habitat keys live in the manifest as the suffix after "scene_".
  const suffix = scene.startsWith('scene_') ? scene.slice('scene_'.length) : scene;
  const habitatKeys: SceneAmbienceKey[] = [
    'blossom', 'beach', 'autumn', 'starry',
    'magical_forest', 'cloud_kingdom', 'underwater',
  ];
  return (habitatKeys as string[]).includes(suffix)
    ? (suffix as SceneAmbienceKey)
    : 'meadow_day';
}

export interface AppContext {
  child: Child;
  balance: number;
  activePet: Pet | null;
  activeScene: string;
  playGate: PlayGate;
  careerPlayDays: number;
  careerStats: CareerStats;
  mailUnread: number;
  refresh: () => Promise<void>;
}

const EMPTY_PLAY_GATE: PlayGate = { blocked: false, pending: 0, total: 0, chores: [] };
const EMPTY_CAREER_STATS: CareerStats = {
  play_days: 0,
  care_days: 0,
  pet_count: 0,
  completed_tasks: 0,
  completed_chores: 0,
  login_days: 0,
  minigame_days: 0,
};
const PARENT_INVITE_NUDGE_KEY = 'xpb.parentInviteNudge';

export default function App() {
  const [context, setContext] = useState<AppContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [showParentInviteNudge, setShowParentInviteNudge] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  async function loadMe() {
    const r = await api.getMe();
    // Sync the parent-forced UI language (or clear it). Must run before any
    // translated UI mounts so the right strings show on first paint.
    applyForcedLang(r.child.forced_language ?? null);
    // v0.12.0 — pin i18n age tier from this child's birth_date so all
    // tier-suffixed copy variants resolve correctly. Default tier is
    // 'young' (matches pre-v0.12.0 hardcoded 9yo tone) when birth_date
    // is missing.
    setI18nAgeTier(getAgeTierFromBirthDate(r.child.birth_date));
    setContext({
      child: r.child,
      balance: r.balance,
      activePet: r.active_pet,
      activeScene: r.active_scene || 'meadow',
      playGate: r.play_gate || EMPTY_PLAY_GATE,
      careerPlayDays: Number(r.career_play_days || 0),
      careerStats: {
        ...EMPTY_CAREER_STATS,
        ...(r.career_stats || {}),
        play_days: Number(r.career_stats?.play_days ?? r.career_play_days ?? 0),
      },
      mailUnread: Number(r.mail_unread || 0),
      refresh: loadMe,
    });
    // Pull parent-configured wind-down threshold so SleepOverlay flips at the
    // right time. Don't block render on this — silently keep the cached value
    // (or default) if the call fails.
    api.getMyParentalControls()
      .then(c => {
        setSleepThresholdMinutes(c.controls.daily_play_minutes);
        // Mirror parent-configured period into the play-session client so
        // SleepOverlay's reset logic uses the right window (default
        // 'hours'/12 keeps the legacy behaviour).
        setPlayPeriod(
          c.controls.play_period_type ?? 'hours',
          c.controls.play_period_hours ?? 12,
        );
      })
      .catch(() => { /* keep last known threshold */ });
  }

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }

    // Did the user just tap the PWA "update" banner? If so, the very next
    // /me request might transiently fail because the SW just swapped out
    // and worker cold-start can race the activation. We give it ONE
    // automatic retry before considering it real, and we never treat a
    // non-401/403 error during this window as an auth problem.
    let justUpdated = false;
    try {
      justUpdated = sessionStorage.getItem('xpb.justUpdated') !== null;
      sessionStorage.removeItem('xpb.justUpdated');
    } catch { /* ignore */ }

    function handleBootError(e: unknown) {
      const status = e instanceof ApiCallError ? e.status : 0;
      // ONLY clear when the server explicitly says "your token is no good".
      // Network errors, server 5xxs, worker cold-start timeouts → keep the
      // token; the next refresh / retry will succeed.
      if (status === 401 || status === 403) clearSession();
    }

    const boot = justUpdated
      ? loadMe().catch((e) => {
          // First attempt failed right after a PWA update. Auth errors
          // still kill the session immediately — but transient network /
          // 5xx errors get one quiet retry before we give up.
          const status = e instanceof ApiCallError ? e.status : 0;
          if (status === 401 || status === 403) { clearSession(); throw e; }
          return new Promise<void>((res) => window.setTimeout(res, 800))
            .then(() => loadMe())
            .catch(handleBootError);
        })
      : loadMe().catch(handleBootError);

    boot.finally(() => setLoading(false));

    // Best-effort: ask the browser to mark our storage as persistent so
    // iOS doesn't evict it after 7 days of inactivity. Returns false on
    // browsers that don't support it; we don't care either way.
    if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
      navigator.storage.persist().catch(() => { /* ignore */ });
    }
  }, []);

  useEffect(() => {
    if (!context) return;
    reportCurrentDeviceSoon();
  }, [context]);

  useEffect(() => {
    if (!context || context.child.display_name == null) return;
    try {
      setShowParentInviteNudge(localStorage.getItem(PARENT_INVITE_NUDGE_KEY) === '1');
    } catch {
      setShowParentInviteNudge(false);
    }
  }, [context?.child.id, context?.child.display_name]);

  // Drive the looping scene-ambience layer based on which page the child is on
  // and (for HomePage) which habitat scene + time-of-day they have active.
  useEffect(() => {
    const key = resolveAmbienceKey(location.pathname, context?.activeScene);
    playSceneAmbience(key);
  }, [location.pathname, context?.activeScene]);

  if (loading) {
    return (
      <>
        <UpdateBanner />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <span className="spinner spinner-lg" />
        </div>
      </>
    );
  }

  if (!context) {
    return (
      <>
        <UpdateBanner />
        <LangSwitcher variant="login" />
        <Routes>
          <Route path="/login" element={<LoginPage onLogin={loadMe} />} />
          <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
        </Routes>
      </>
    );
  }

  return (
    <AppShellV2 ctx={context}>
      <UpdateBanner />
      {context.child.display_name == null && (
        <NicknameSetupModal
          loginName={context.child.name}
          onDone={loadMe}
        />
      )}
      <BirthdaySurpriseModal
        childId={context.child.id}
        enabled={context.child.display_name != null}
        onClaimed={loadMe}
      />
      {showParentInviteNudge && context.child.display_name != null && (
        <ParentInviteNudge
          onClose={() => {
            try { localStorage.removeItem(PARENT_INVITE_NUDGE_KEY); } catch { /* ignore */ }
            setShowParentInviteNudge(false);
          }}
          onInvite={() => {
            try { localStorage.removeItem(PARENT_INVITE_NUDGE_KEY); } catch { /* ignore */ }
            setShowParentInviteNudge(false);
            navigate('/invite');
          }}
        />
      )}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<ParkPage ctx={context} />} />
          <Route path="/chat" element={<ChatPage ctx={context} />} />
          {/* PR-8 — /wallet is now /me/wallet. Old path redirects. */}
          <Route path="/wallet" element={<Navigate to="/me/wallet" replace />} />
          {/* v0.14.0 PR-6 — old standalone routes redirect into the
              new / Park tabs so PWA-cached deep links and old shortcuts
              still resolve. Pokedex / Quiz / Imagine / Wallet / Timeline
              stay at their old paths until LearnPage and MePage are
              refactored in a follow-up (out of v0.14.0 scope). */}
          <Route path="/shop" element={<Navigate to="/?tab=shop" replace />} />
          <Route path="/zoo" element={<Navigate to="/?tab=zoo" replace />} />
          <Route path="/games" element={<Navigate to="/?tab=games" replace />} />

          <Route path="/pokedex" element={<PokedexPage />} />
          <Route path="/adopt" element={<Navigate to="/?tab=shop" replace />} />
          <Route path="/journal" element={<JournalPage ctx={context} />} />
          <Route path="/imagine" element={<Navigate to="/learn?tab=imagine" replace />} />
          <Route path="/quiz" element={<Navigate to="/learn?tab=quiz" replace />} />
          <Route path="/habits" element={<Navigate to="/schedule?filter=chore" replace />} />
          <Route path="/mailbox" element={<Navigate to="/chat?thread=parent" replace />} />
          <Route path="/invite" element={<InvitePage ctx={context} />} />
          <Route path="/games/math-blitz" element={<MathBlitzPage ctx={context} />} />
          <Route path="/games/memory-digits" element={<MemoryDigitsPage ctx={context} />} />
          <Route path="/games/potion-puzzle" element={<PotionPuzzlePage ctx={context} />} />
          <Route path="/games/:gameCode" element={<AgeMiniGamePage ctx={context} />} />
          <Route path="/building/:buildingId" element={<BuildingInteriorPage />} />
          <Route path="/onboarding/quiz" element={<Navigate to="/onboarding/hatch" replace />} />
          <Route path="/onboarding/hatch" element={<OnboardingHatchPage />} />
          {/* PR-8 — /me is now an umbrella with sub-routes. Settings live
              at /me/settings; /wallet /timeline /profile old paths
              redirect into /me/* equivalents. */}
          <Route path="/me" element={<MePage ctx={context} />} />
          <Route path="/me/settings" element={<MeSettingsPage ctx={context} onLogout={() => setContext(null)} />} />
          <Route path="/me/wallet" element={<WalletPage ctx={context} />} />
          <Route path="/me/story" element={<TimelinePage />} />
          <Route path="/me/invite" element={<InvitePage ctx={context} />} />
          <Route path="/me/profile" element={<ProfilePage ctx={context} />} />
          <Route path="/profile" element={<Navigate to="/me/profile" replace />} />
          <Route path="/timeline" element={<Navigate to="/me/story" replace />} />
          <Route path="/pet/:id" element={<PetProfilePage />} />
          {/* v0.14.0 — new top-level destinations. /park ships in PR-2;
              /schedule + /learn land in PR-4. Placeholders stay so the
              Dock buttons remain clickable without 404s. */}
          <Route path="/schedule" element={<SchedulePage ctx={context} />} />
          {/* /park existed as its own dock entry in PR-2; PR-2.5 folds it
              into "/" as tabbed views. Keep the path 200-ing for PWA
              cache compatibility — default to the zoo tab. */}
          <Route path="/park" element={<Navigate to="/?tab=zoo" replace />} />
          <Route path="/learn" element={<LearnPage ctx={context} />} />
          {/* v0.11.0 — design system preview (not in nav) */}
          <Route path="/design-preview" element={<DesignPreviewPage />} />
          <Route path="/ccc" element={<DebugAcceleratePage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <SleepOverlay />
      <RewardBurst />
    </AppShellV2>
  );
}

function ParentInviteNudge({ onClose, onInvite }: { onClose: () => void; onInvite: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('auth.child.parentNudge.title')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2600,
        display: 'grid',
        placeItems: 'center',
        padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(20px + env(safe-area-inset-bottom))',
        background: 'rgba(51, 36, 23, .36)',
      }}
      onClick={onClose}
    >
      <div
        className="pop-in"
        style={{
          width: 'min(92vw, 420px)',
          borderRadius: 24,
          background: '#fffdf7',
          border: '1px solid rgba(217, 119, 6, .22)',
          boxShadow: '0 24px 70px rgba(80, 52, 22, .25)',
          padding: 22,
          color: 'var(--xpb-text-primary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 10 }}>👨‍👩‍👧</div>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
          {t('auth.child.parentNudge.title')}
        </div>
        <div style={{ fontSize: 14, color: 'var(--xpb-text-secondary)', lineHeight: 1.65, marginBottom: 18 }}>
          {t('auth.child.parentNudge.body')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: '1px solid var(--xpb-border)',
              background: '#fff',
              color: 'var(--xpb-text-secondary)',
              borderRadius: 16,
              minHeight: 48,
              fontWeight: 700,
            }}
          >
            {t('auth.child.parentNudge.later')}
          </button>
          <button
            type="button"
            onClick={onInvite}
            style={{
              border: 'none',
              background: 'linear-gradient(180deg, #f59e0b, #d97706)',
              color: '#fff',
              borderRadius: 16,
              minHeight: 48,
              fontWeight: 800,
              boxShadow: '0 10px 24px rgba(217,119,6,.22)',
            }}
          >
            {t('auth.child.parentNudge.cta')}
          </button>
        </div>
      </div>
    </div>
  );
}

function RouteFallback() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <span className="spinner spinner-lg" />
    </div>
  );
}

// (PR-1 ComingSoonPage placeholder removed in PR-7 once /schedule
// /park /learn all had real pages.)
